import { Anchor, Badge, Divider, Loader, Stack, Table, Title } from "@mantine/core";
import { Link } from "@tanstack/react-router";

import { useListStaffTeams } from "../../api/generated/endpoints/staff/staff";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { formatCount, formatDate } from "../../ui/format";
import { CreateTeamForm } from "./CreateTeamForm";
import { FindUserForm } from "./FindUserForm";
import { PartialCost, Unknown, formatUsd } from "./usage-format";
import classes from "./usage.module.css";

/**
 * A team's lifetime vendor cost (spec usage-analytics 3): the known part, flagged "partial" while
 * some job's is unknown; $0.00 for a team whose jobs cost nothing, never for an unknown cost.
 */
function LifetimeCost({ usd, incomplete }: { usd: string | null; incomplete: number }) {
  if (usd === null) return incomplete > 0 ? <Unknown /> : <span className={classes.zero}>{formatUsd("0")}</span>;
  return (
    <>
      <span>{formatUsd(usd)}</span> {incomplete > 0 ? <PartialCost jobs={incomplete} /> : null}
    </>
  );
}

/**
 * Every team, deleted ones included and flagged, with its lifetime credits used and vendor cost;
 * create one for an owner; find a user.
 */
export function StaffTeamsScreen() {
  const teams = useListStaffTeams();
  return (
    <Stack gap="lg">
      <CreateTeamForm />
      <FindUserForm />
      <Divider />
      <Title order={2} size="h4">
        Teams
      </Title>
      {teams.isPending ? <Loader size="sm" aria-label="Loading teams" /> : null}
      {teams.isError ? <ErrorAlert error={teams.error} /> : null}
      {teams.data === undefined ? null : (
        <Table.ScrollContainer minWidth={560}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Team</Table.Th>
                <Table.Th>Members</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th className={classes.num} title="Charged less refunded, over its life">
                  Credits used
                </Table.Th>
                <Table.Th className={classes.num} title="The known cost in USD, over its life">
                  Vendor cost
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teams.data.map((team) => (
                <Table.Tr key={team.id}>
                  <Table.Td>
                    <Anchor
                      renderRoot={(props) => (
                        <Link {...props} to="/staff/teams/$teamId" params={{ teamId: team.id }} />
                      )}
                    >
                      {team.name}
                    </Anchor>{" "}
                    {team.held_by_staff ? (
                      <Badge size="xs" variant="light">
                        Waiting for its owner
                      </Badge>
                    ) : null}{" "}
                    {team.deleted_at === null ? null : (
                      <Badge size="xs" color="gray">
                        Deleted
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{team.member_count}</Table.Td>
                  <Table.Td>{formatDate(team.created_at)}</Table.Td>
                  <Table.Td className={classes.num}>
                    <span className={team.credits_used === 0 ? classes.zero : undefined}>
                      {formatCount(team.credits_used)}
                    </span>
                  </Table.Td>
                  <Table.Td className={classes.num}>
                    <LifetimeCost usd={team.vendor_cost_usd} incomplete={team.cost_incomplete} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
